import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Small load-state wrapper so screens do not scatter `useEffect(fetch)`.
 *
 * Returns `{ status, data, error, reload }` where status is one of
 * 'idle' | 'loading' | 'error' | 'ready' — loading, error and empty are distinct renders;
 * "empty" is the caller's decision on a `ready` payload, never folded into loading.
 *
 * `loader` is re-created by the caller whenever its inputs change; pass those inputs in
 * `deps` so a stale response from a superseded load is discarded (last-write-wins by sequence).
 * `enabled: false` keeps the resource 'idle' (e.g. no active project).
 */
export function useResource(loader, deps, { enabled = true } = {}) {
    const [state, setState] = useState({ status: enabled ? 'loading' : 'idle', data: null, error: null });
    const seq = useRef(0);

    const load = useCallback(async () => {
        const mine = ++seq.current;
        setState((prev) => ({ status: 'loading', data: prev.data, error: null }));
        try {
            const data = await loader();
            if (mine === seq.current) setState({ status: 'ready', data, error: null });
        } catch (error) {
            if (mine === seq.current) setState({ status: 'error', data: null, error });
        }
    }, deps); // loader identity is governed by the caller's deps (no react-hooks lint rule is loaded in this build)

    useEffect(() => {
        if (!enabled) {
            setState({ status: 'idle', data: null, error: null });
            return undefined;
        }
        load();
        return () => {
            seq.current += 1;
        };
    }, [load, enabled]);

    return { ...state, reload: load };
}
